/**
 * ServiceHistoryPage — read-only list of services the user has received.
 *
 * Route: `/service-history` (ProtectedRoute).
 *
 * Distinct from /transactions:
 *   - /transactions     → payment records (money out)
 *   - /service-history  → procedures actually performed (treatment received)
 *
 * The customer-facing app is **read-only** here; entries are created
 * by the provider PWA after a clinician records the procedure. The
 * mock seeds two completed entries so the page renders populated.
 *
 * Same structural pattern as TransactionsPage (rich row cards, no
 * filter, TanStack Query, locale-aware dates). Fewer fields per row:
 * no amount, no gateway, no tracking number.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Chip,
  Box,
  IconButton,
  Skeleton,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventIcon from '@mui/icons-material/Event';
import RefreshIcon from '@mui/icons-material/Refresh';
import InboxIcon from '@mui/icons-material/Inbox';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';

import LanguageToggle from '@/components/LanguageToggle';
import SupportChooserDialog from '@/components/SupportChooserDialog';
import { serviceHistoryApi } from '@/services/api';
import { selectLanguage, selectIsRtl } from '@/store/uiSlice';
import { formatDate } from '@/utils/date';
import { localized } from '@/utils/localizedContent';

/* ------------------------------------------------------------------ */
/* Sub-components                                                     */
/* ------------------------------------------------------------------ */

/**
 * Map history status → MUI Chip color. Unknown statuses render neutral
 * so an unrecognized status (e.g. future 'no_show') won't crash.
 */
function statusChipColor(status) {
  switch (status) {
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    case 'no_show':   return 'warning';
    default:          return 'default';
  }
}

function HistoryCard({ entry, language, t, onContactSupport, onViewReport }) {
  const serviceName = localized(entry.serviceName, language);
  const hasReport = Boolean(entry.analysisId) || entry.serviceType === 'face_analysis';

  return (
    <Card variant="outlined">
      <CardContent>
        {/* Top row: status chip + service name */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={1}
          sx={{ mb: 1 }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {serviceName}
          </Typography>
          <Chip
            size="small"
            color={statusChipColor(entry.status)}
            label={t(`serviceHistory.status.${entry.status}`, {
              defaultValue: entry.status,
            })}
            variant="filled"
          />
        </Stack>

        {/* Location */}
        {entry.location && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ color: 'text.secondary', mb: 1 }}
          >
            <StorefrontIcon fontSize="small" />
            <Typography variant="body2">{entry.location}</Typography>
          </Stack>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Performed at */}
        {entry.performedAt && (
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ color: 'text.secondary' }}
          >
            <EventIcon fontSize="small" />
            <Typography variant="caption">
              {formatDate(entry.performedAt, language, { style: 'datetime' })}
            </Typography>
          </Stack>
        )}

        {/* Contact-support link — opens NewTicketPage with relatedType
            + relatedId in the query string so admins know which
            procedure the user is asking about. Subtle Button variant
            keeps it from competing with the entry's data. */}
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          {hasReport && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AssessmentIcon fontSize="small" />}
              disabled={!entry.analysisId}
              onClick={() => onViewReport(entry.analysisId)}
            >
              {t('serviceHistory.viewReport')}
            </Button>
          )}
          <Button
            size="small"
            variant="text"
            startIcon={<ContactSupportIcon fontSize="small" />}
            onClick={() => onContactSupport(entry.id)}
          >
            {t('serviceHistory.contactSupport')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

export function ServiceHistoryPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const language = useSelector(selectLanguage);
  const isRtl = useSelector(selectIsRtl);

  // Which history entry the support chooser is open for (null = closed).
  const [supportEntryId, setSupportEntryId] = useState(null);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['service-history'],
    queryFn: () => serviceHistoryApi.listHistory(),
    // Provider PWA writes here — refresh at a reasonable cadence so a
    // freshly-completed procedure shows up without a manual reload.
    staleTime: 60 * 1000,
  });

  const history = data?.data?.history ?? [];

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <HistoryIcon color="primary" />
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('serviceHistory.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Subtitle — only when we have something to show */}
        {!isError && (history.length > 0 || isLoading) && (
          <Typography variant="body2" color="text.secondary">
            {t('serviceHistory.subtitle')}
          </Typography>
        )}

        {/* Loading skeleton */}
        {isLoading && (
          <Stack spacing={2}>
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={130}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        )}

        {/* Error */}
        {isError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('serviceHistory.error.loadFailed')}
          </Alert>
        )}

        {/* Empty state */}
        {!isLoading && !isError && history.length === 0 && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <InboxIcon sx={{ fontSize: 56, opacity: 0.4 }} />
            <Typography variant="subtitle1" textAlign="center" fontWeight={600}>
              {t('serviceHistory.empty.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 320 }}
            >
              {t('serviceHistory.empty.message')}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate('/services')}
              sx={{ mt: 1 }}
            >
              {t('serviceHistory.empty.browseServices')}
            </Button>
          </Stack>
        )}

        {/* List */}
        {!isLoading && !isError && history.length > 0 && (
          <Stack spacing={2}>
            {history.map((entry) => (
              <HistoryCard
                key={entry.id}
                entry={entry}
                language={language}
                t={t}
                onContactSupport={(entryId) => setSupportEntryId(entryId)}
                onViewReport={(analysisId) => navigate(`/report/${encodeURIComponent(analysisId)}`)}
              />
            ))}
          </Stack>
        )}
      </Stack>

      {/* Support channel picker — from a history record the user can reach
          either the app team or the clinic; we pass the record link along
          so the chosen "new ticket" form is pre-associated with it. */}
      <SupportChooserDialog
        open={supportEntryId != null}
        onClose={() => setSupportEntryId(null)}
        onChoose={(domain) => {
          const id = supportEntryId;
          setSupportEntryId(null);
          const base = domain === 'clinic' ? '/clinic-tickets/new' : '/support/new';
          navigate(`${base}?relatedType=service_history&relatedId=${id}`);
        }}
      />
    </Container>
  );
}

export default ServiceHistoryPage;
